'use client'

import {
  MAX_SELECTABLES_COLUMNS,
  useReferencesProduct,
} from '@/feature/product/hooks/useReferencesProduct'
import { referencesProductIsMonoDiscriminantAtom } from '@/feature/product/services/referencesProduct.store'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { Form } from '@/ui/form'
import { useForm } from '@/ui/form/Form.client'
import { Select } from '@/ui/form/inputs/select/Select.client'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'

function trackColumnSelectionChange(previous: string[], next: string[]) {
  const removedColumns = previous.filter((column) => !next.includes(column))
  const addedColumns = next.filter((column) => !previous.includes(column))

  removedColumns.forEach((column) => {
    pushToGtmDataLayer({
      event: 'gaEvent',
      eventCategory: 'fiche produit multi-discriminant',
      eventAction: 'tableau de références - retrait discriminant',
      eventLabel: String(column),
    })
  })

  addedColumns.forEach((column) => {
    pushToGtmDataLayer({
      event: 'gaEvent',
      eventCategory: 'fiche produit multi-discriminant',
      eventAction: 'tableau de références - ajout discriminant',
      eventLabel: String(column),
    })
  })
}

export function ReferencesProductColumnsMenu(): React.ReactElement | null {
  const { t } = useTranslation(['productPage'])
  const { selectedColumns, setSelectedColumns, selectableColumns } = useReferencesProduct()
  const isMonoDiscriminant = useAtomValue(referencesProductIsMonoDiscriminantAtom)
  const form = useForm({
    values: { columns: selectedColumns },
  })
  const items = useMemo(
    () => selectableColumns.map((columnLabel) => ({ id: columnLabel, label: columnLabel })),
    [selectableColumns],
  )
  const triggerLabel = t('productPage:referencesProductSection.editColumns.label')
  const headerMessage = t('productPage:referencesProductSection.editColumns.title', {
    totalColumns: Math.min(selectableColumns.length, MAX_SELECTABLES_COLUMNS),
  })

  if (selectableColumns.length <= 1) {
    return null
  }

  return (
    <Form form={form} onSubmitAction={() => undefined} className="w-max">
      <Select
        name="columns"
        items={items}
        label=""
        placeholder={triggerLabel}
        aria-label={triggerLabel}
        selectionMode="multiple"
        withTags={false}
        headerMessage={headerMessage}
        minSelected={1}
        maxSelected={MAX_SELECTABLES_COLUMNS}
        maxHeight={320}
        className="w-max"
        data-testid="references-product-columns-menu"
        onValueChange={(value) => {
          if (!Array.isArray(value)) return

          if (!isMonoDiscriminant) {
            trackColumnSelectionChange(selectedColumns, value)
          }

          setSelectedColumns(value)
        }}
      />
    </Form>
  )
}
